import { InvoiceTransformationRequestUi } from '@monetize/types/app';
import { FC, useMemo } from 'react';
import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';
import { IoMdTrash } from 'react-icons/io';
import { MdInfo } from 'react-icons/md';
import { ContactSelect } from '../Contacts/ContactSelect';
import {
  LanguageLocaleDropdown,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInput,
  MText,
  MTooltip,
} from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import {
  BillGroupOverrideKey,
  isContactOverrideKey,
  OVERRIDE_KEY_OPTIONS,
} from './invoiceTransformation.utils';

interface InvoiceTransformationOverridesProps {
  lineRuleIndex: number;
  accountId: string;
  formContext: UseFormReturn<InvoiceTransformationRequestUi>;
  isReadOnly: boolean;
}

export const InvoiceTransformationOverrides: FC<
  InvoiceTransformationOverridesProps
> = ({ lineRuleIndex, accountId, formContext, isReadOnly }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = formContext;

  const path = `invoiceLineRules.${lineRuleIndex}.billGroupOverrides` as const;

  const { fields, append, remove } = useFieldArray({
    name: path,
    control,
  });

  const rows = watch(path);

  const availableKeysByIndex = useMemo(() => {
    const currentRows = rows ?? [];
    return fields.map((_, rowIndex) => {
      const usedByOthers = new Set(
        currentRows
          .map((row, idx) => (idx !== rowIndex ? row?.key : undefined))
          .filter((key): key is BillGroupOverrideKey => Boolean(key)),
      );
      return OVERRIDE_KEY_OPTIONS.filter(
        (option) => !usedByOthers.has(option.value),
      );
    });
  }, [fields, rows]);

  const overrideErrors =
    errors.invoiceLineRules?.[lineRuleIndex]?.billGroupOverrides;

  const canAddMore = fields.length < OVERRIDE_KEY_OPTIONS.length;

  return (
    <>
      <MFlex align="center" mb={2}>
        <MText fontWeight="semibold">Bill Group Overrides</MText>
        <MTooltip
          label="Specify field values that you would like to use on invoices instead of the default values from the Bill Group"
          placement="top"
        >
          <MFlex align="center">
            <MIcon as={MdInfo} color="tPurple.base" ml="1" />
          </MFlex>
        </MTooltip>
      </MFlex>
      {fields.map((fieldItem, rowIndex) => {
        const currentKey = rows?.[rowIndex]?.key;
        const isLocale = currentKey === 'locale';
        const isContact = currentKey && isContactOverrideKey(currentKey);
        return (
          <MGrid
            key={fieldItem.id}
            templateColumns="1fr 1fr auto"
            gap={2}
            alignItems="start"
            mb={1}
          >
            <MGridItem>
              <MFormField error={overrideErrors?.[rowIndex]?.key} isRequired>
                <Controller
                  name={`${path}.${rowIndex}.key`}
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      placeholder="Select field"
                      items={availableKeysByIndex[rowIndex]}
                      itemTitle="title"
                      itemValue="value"
                      isDisabled={isReadOnly}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem>
              <MFormField error={overrideErrors?.[rowIndex]?.value} isRequired>
                <Controller
                  name={`${path}.${rowIndex}.value`}
                  control={control}
                  render={({ field: { value, ...rest } }) => {
                    if (isLocale) {
                      return (
                        <LanguageLocaleDropdown
                          placeholder="Select Language"
                          value={value ?? ''}
                          isDisabled={isReadOnly}
                          {...rest}
                        />
                      );
                    }
                    if (isContact) {
                      return (
                        <ContactSelect
                          accountId={accountId}
                          value={value ?? ''}
                          isDisabled={isReadOnly}
                          {...rest}
                        />
                      );
                    }
                    return (
                      <MInput
                        placeholder="Value"
                        value={value ?? ''}
                        isDisabled={isReadOnly}
                        {...rest}
                      />
                    );
                  }}
                />
              </MFormField>
            </MGridItem>
            <MGridItem>
              <MFlex pt={1}>
                {!isReadOnly && (
                  <MCustomIconButton
                    variant="icon"
                    icon={IoMdTrash}
                    onClick={() => remove(rowIndex)}
                    iconColor="tRed.base"
                    aria-label="Remove override"
                  />
                )}
              </MFlex>
            </MGridItem>
          </MGrid>
        );
      })}
      {!isReadOnly && (
        <MButton
          variant="tertiary"
          size="sm"
          mt={1}
          isDisabled={!canAddMore}
          onClick={() => append({ key: '' as BillGroupOverrideKey, value: '' })}
        >
          + Add Override
        </MButton>
      )}
    </>
  );
};
